// components/Admin/UsersPermissions.jsx
import React, { useState, useEffect, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import { usersPermissionsService } from '../../services/adminService';
import {
  UserGroupIcon,
  CheckCircleIcon,
  EyeIcon,
  KeyIcon
} from '@heroicons/react/24/solid';
import { motion, AnimatePresence } from 'framer-motion';


const UsersPermissions = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await usersPermissionsService.getUsers();
      setUsers(data);
    } catch (error) {
      showMessage('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPermissions = async (user) => {
    try {
      const permissions = await usersPermissionsService.getUserPermissions(user.id);
      setSelectedUser({ ...user, ...permissions });
      setShowPermissionsModal(true);
    } catch (error) {
      showMessage('Failed to load user permissions', 'error');
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      const result = await usersPermissionsService.toggleUserStatus(userId);
      showMessage(result.message, 'success');
      loadUsers(); // Reload users
    } catch (error) {
      showMessage('Failed to update user status', 'error');
    }
  };

  const handleUpdatePermissions = async (modules) => {
    try {
      const result = await usersPermissionsService.updatePermissions({
        userId: selectedUser.id,
        modules: modules
      });

      showMessage(result.message, 'success');
      setShowPermissionsModal(false);
      loadUsers(); // Reload users to reflect changes
    } catch (error) {
      showMessage(error.message || 'Failed to update permissions', 'error');
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const roleTone = (roles) => {
    if (roles.includes('SuperAdmin')) return 'blue';
    if (roles.includes('CompanyAdmin')) return 'teal';
    return 'grey';
  };

  const roleLabel = (roles) =>
    roles.includes('SuperAdmin') ? 'Super Admin' :
    roles.includes('CompanyAdmin') ? 'Company Admin' : 'Employee';

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(u =>
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.companyName?.toLowerCase().includes(q)
    );
  }, [users, search]);

  const badge = (tone) => ({
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    background: `var(--b-${tone}-bg)`,
    color: `var(--b-${tone}-fg)`
  });

  const columns = [
    {
      name: 'User',
      selector: row => `${row.firstName} ${row.lastName}`,
      sortable: true,
      cell: row => (
        <div className="flex items-center gap-3 py-1">
          <img
            className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
            style={{ border: '1px solid var(--line)' }}
            src={row.profilePictureUrl || '/api/placeholder/32/32'}
            alt={`${row.firstName} ${row.lastName}`}
            onError={(e) => {
              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(row.firstName + ' ' + row.lastName)}&background=00696B&color=fff&size=32`;
            }}
          />
          <div className="min-w-0">
            <div className="truncate text-[12.5px] font-semibold" style={{ color: 'var(--ink)' }}>
              {row.firstName} {row.lastName}
            </div>
            <div className="truncate text-[11px]" style={{ color: 'var(--faint)' }}>{row.email}</div>
          </div>
        </div>
      ),
      minWidth: '220px',
      grow: 1
    },
    {
      name: 'Company',
      selector: row => row.companyName,
      sortable: true,
      cell: row => (
        <span className="text-[12.5px]" style={{ color: row.companyName ? 'var(--body-2)' : 'var(--dim)' }}>
          {row.companyName || 'N/A'}
        </span>
      ),
      minWidth: '150px'
    },
    {
      name: 'Role',
      selector: row => roleLabel(row.roles),
      sortable: true,
      cell: row => <span style={badge(roleTone(row.roles))}>{roleLabel(row.roles)}</span>,
      width: '140px'
    },
    {
      name: 'Status',
      selector: row => row.isActive,
      sortable: true,
      cell: row => <span style={badge(row.isActive ? 'green' : 'red')}>{row.isActive ? 'Active' : 'Inactive'}</span>,
      width: '100px'
    },
    {
      name: 'Joined',
      selector: row => row.createdDate,
      sortable: true,
      cell: row => (
        <span className="text-[12px]" style={{ color: 'var(--body-2)' }}>
          {new Date(row.createdDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
        </span>
      ),
      minWidth: '120px'
    },
    {
      name: '',
      cell: row => (
        <div className="flex items-center justify-end gap-1.5">
          <button
            onClick={() => handleSetPermissions(row)}
            className="rounded-[3px] p-1.5 transition-colors"
            style={{ color: 'var(--accent)' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            title="Set Permissions"
          >
            <KeyIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleToggleStatus(row.id)}
            className="rounded-[3px] p-1.5 transition-colors"
            style={{ color: row.isActive ? 'var(--neg)' : 'var(--pos)' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            title={row.isActive ? 'Deactivate User' : 'Activate User'}
          >
            <EyeIcon className="h-4 w-4" />
          </button>
        </div>
      ),
      width: '90px',
      ignoreRowClick: true,
      allowOverflow: true,
      button: true
    }
  ];

  const customStyles = {
    table: { style: { backgroundColor: 'var(--surface)' } },
    head: {
      style: {
        fontSize: '11.5px',
        fontWeight: '600',
        color: 'var(--muted)',
        backgroundColor: 'var(--surface-2)'
      }
    },
    headRow: {
      style: {
        minHeight: '32px',
        borderBottomWidth: '1px',
        borderBottomColor: 'var(--line)',
        backgroundColor: 'var(--surface-2)'
      }
    },
    headCells: {
      style: {
        paddingLeft: '10px',
        paddingRight: '10px',
        fontWeight: '600',
        letterSpacing: '0',
        textTransform: 'none',
        fontSize: '11.5px',
        color: 'var(--muted)',
        backgroundColor: 'var(--surface-2)'
      }
    },
    cells: {
      style: {
        paddingLeft: '10px',
        paddingRight: '10px',
        paddingTop: '8px',
        paddingBottom: '8px',
        fontSize: '12.5px',
        color: 'var(--body-2)'
      }
    },
    rows: {
      style: {
        minHeight: '48px',
        backgroundColor: 'var(--surface)',
        color: 'var(--body-2)',
        '&:not(:last-of-type)': {
          borderBottomWidth: '1px',
          borderBottomColor: 'var(--line-soft)'
        },
        '&:hover': { backgroundColor: 'var(--row-sel)' }
      },
      stripedStyle: { backgroundColor: 'var(--surface)' }
    },
    pagination: {
      style: {
        backgroundColor: 'var(--surface)',
        color: 'var(--muted)',
        fontSize: '12px',
        borderTopWidth: '1px',
        borderTopColor: 'var(--line)',
        minHeight: '42px'
      },
      pageButtonsStyle: {
        fill: 'var(--muted)',
        '&:disabled': { fill: 'var(--dim)' },
        '&:hover:not(:disabled)': { backgroundColor: 'var(--hover)' }
      }
    },
    noData: { style: { backgroundColor: 'var(--surface)' } },
    progress: { style: { backgroundColor: 'var(--surface)' } }
  };

  const LoadingComponent = () => (
    <div className="flex items-center justify-center py-12" style={{ background: 'var(--surface)' }}>
      <div className="h-6 w-6 animate-spin rounded-full border-b-2" style={{ borderColor: 'var(--accent)' }} />
      <span className="ml-3 text-[12.5px]" style={{ color: 'var(--muted)' }}>Loading users…</span>
    </div>
  );

  const NoDataComponent = () => (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center" style={{ background: 'var(--surface)' }}>
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full" style={{ background: 'var(--tint)' }}>
        <UserGroupIcon className="h-6 w-6" style={{ color: 'var(--tint-ink)' }} />
      </div>
      <h3 className="mb-1 text-[14px] font-semibold" style={{ color: 'var(--ink)' }}>No users found</h3>
      <p className="text-[12.5px]" style={{ color: 'var(--faint)' }}>There are no users in the system yet.</p>
    </div>
  );

  return (
    <div className="min-h-full" style={{ background: 'var(--bg)' }}>
      {/* ══ CONTROL PANEL ══ */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-[14px] pb-2 pt-[9px]">
          <h1 className="m-0 truncate text-[18px] font-semibold tracking-[-.2px]" style={{ color: 'var(--ink)' }}>
            Users &amp; Permissions
          </h1>

          <div className="min-w-[100px] flex-1" />

          {/* Search */}
          <div
            className="flex min-w-[200px] max-w-[420px] flex-1 items-center gap-[6px] rounded-[3px] py-[2px] pl-2 pr-1"
            style={{ background: 'var(--surface)', border: '1px solid var(--line-3)' }}
          >
            <svg className="h-[13px] w-[13px] flex-shrink-0" style={{ color: 'var(--faint)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              placeholder="Search name, email or company…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-6 min-w-[70px] flex-1 border-0 bg-transparent text-[12.5px] outline-none"
              style={{ color: 'var(--body)' }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="px-1 text-[14px] leading-none"
                style={{ color: 'var(--faint)', background: 'transparent', border: 0, cursor: 'pointer' }}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>

          {/* Record counter */}
          <div className="flex items-center gap-[10px] text-[12px]" style={{ color: 'var(--muted)' }}>
            <span>{filteredUsers.length}{search ? ` / ${users.length}` : ''} record{filteredUsers.length === 1 ? '' : 's'}</span>
            <span style={{ width: 1, height: 15, background: 'var(--line-3)' }} />
            <span>{users.filter(u => u.roles.includes('CompanyAdmin')).length} company admins</span>
            <span style={{ width: 1, height: 15, background: 'var(--line-3)' }} />
            <span>{users.filter(u => u.isActive).length} active</span>
          </div>
        </div>
      </div>

      <div className="p-[14px]">
        {message.text && (
          <div
            className="mb-[14px] flex items-center gap-2 rounded-[4px] px-3 py-2.5 text-[12.5px]"
            style={{
              background: message.type === 'success' ? 'var(--b-green-bg)' : 'var(--b-red-bg)',
              border: '1px solid var(--line)',
              color: message.type === 'success' ? 'var(--b-green-fg)' : 'var(--b-red-fg)'
            }}
          >
            <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
            {message.text}
          </div>
        )}

        {/* Table */}
        <div
          className="overflow-hidden rounded-[4px]"
          style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow-sm)' }}
        >
          <DataTable
            columns={columns}
            data={filteredUsers}
            progressPending={loading}
            progressComponent={<LoadingComponent />}
            noDataComponent={<NoDataComponent />}
            customStyles={customStyles}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[5, 10, 25, 50]}
            highlightOnHover
            pointerOnHover
            responsive
            persistTableHead
          />
        </div>
      </div>

      {/* Permissions Modal */}
      <AnimatePresence>
        {showPermissionsModal && selectedUser && (
          <PermissionsModal
            user={selectedUser}
            onClose={() => setShowPermissionsModal(false)}
            onSave={handleUpdatePermissions}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Permissions Modal Component — same behaviour, reskinned to the app's
// teal accent tokens instead of the pink/rose gradient palette.
const PermissionsModal = ({ user, onClose, onSave }) => {
  const [modules, setModules] = useState(user.modules || []);
  const [saving, setSaving] = useState(false);

  const handleToggleModule = (moduleId) => {
    setModules(prev => prev.map(module =>
      module.moduleId === moduleId
        ? { ...module, hasAccess: !module.hasAccess }
        : module
    ));
  };

  const handleSelectAll = () => {
    const allSelected = modules.every(module => module.hasAccess);
    setModules(prev => prev.map(module => ({
      ...module,
      hasAccess: !allSelected
    })));
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(modules);
    setSaving(false);
  };

  const selectedCount = modules.filter(m => m.hasAccess).length;
  const totalCount = modules.length;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,.5)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-4xl overflow-hidden rounded-[6px]"
        style={{ background: 'var(--surface)', boxShadow: 'var(--shadow)', maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6" style={{ background: 'var(--accent)', color: 'var(--on-accent)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img
                className="h-14 w-14 rounded-full object-cover"
                style={{ border: '2px solid var(--on-accent)' }}
                src={user.profilePictureUrl || '/api/placeholder/56/56'}
                alt={user.userName}
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.userName)}&background=fff&color=00696B&size=56`;
                }}
              />
              <div>
                <h2 className="text-xl font-bold">{user.userName}</h2>
                <p className="text-[13px] opacity-90">{user.email}</p>
                <p className="text-[12px] opacity-75">{user.companyName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-2 transition-colors"
              style={{ color: 'var(--on-accent)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,.15)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <span className="text-2xl leading-none">×</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-6" style={{ maxHeight: '60vh' }}>
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h3 className="text-[15px] font-semibold" style={{ color: 'var(--ink)' }}>Module Permissions</h3>
              <p className="text-[12.5px]" style={{ color: 'var(--muted)' }}>Select modules to grant access to this user</p>
            </div>
            <button
              onClick={handleSelectAll}
              className="rounded-[4px] px-4 py-2 text-[12.5px] font-semibold transition-colors"
              style={{ background: 'var(--accent)', color: 'var(--on-accent)' }}
            >
              {modules.every(m => m.hasAccess) ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {modules.map((module) => (
              <motion.div
                key={module.moduleId}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="cursor-pointer rounded-[6px] p-4 transition-all"
                style={{
                  border: module.hasAccess ? '1.5px solid var(--accent)' : '1px solid var(--line)',
                  background: module.hasAccess ? 'var(--tint)' : 'var(--surface)'
                }}
                onClick={() => handleToggleModule(module.moduleId)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold" style={{ color: module.hasAccess ? 'var(--tint-ink)' : 'var(--ink)' }}>
                      {module.moduleName}
                    </h4>
                    <p className="mt-2 text-[12.5px]" style={{ color: module.hasAccess ? 'var(--tint-ink)' : 'var(--muted)' }}>
                      {module.description}
                    </p>
                  </div>
                  <div
                    className="ml-4 flex h-7 w-7 items-center justify-center rounded-full transition-colors"
                    style={{
                      border: module.hasAccess ? '2px solid var(--accent)' : '2px solid var(--line-3)',
                      background: module.hasAccess ? 'var(--accent)' : 'transparent'
                    }}
                  >
                    {module.hasAccess && <CheckCircleIcon className="h-4 w-4" style={{ color: 'var(--on-accent)' }} />}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4" style={{ background: 'var(--surface-2)', borderTop: '1px solid var(--line)' }}>
          <div className="flex items-center justify-between">
            <div className="text-[12.5px]" style={{ color: 'var(--body-2)' }}>
              <span className="font-semibold" style={{ color: 'var(--accent)' }}>{selectedCount}</span> of{' '}
              <span className="font-semibold">{totalCount}</span> modules selected
            </div>
            <div className="flex space-x-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="rounded-[4px] px-6 py-2 text-[12.5px] font-medium transition-colors"
                style={{ background: 'var(--surface)', border: '1px solid var(--line)', color: 'var(--body)' }}
              >
                Cancel
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 rounded-[4px] px-8 py-2 text-[12.5px] font-medium transition-colors disabled:opacity-50"
                style={{ background: 'var(--accent)', color: 'var(--on-accent)' }}
              >
                {saving ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2" style={{ borderColor: 'var(--on-accent)' }} />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-4 w-4" />
                    Save Permissions
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default UsersPermissions;
